import React, { createContext, ReactNode, useReducer } from 'react'
import {
  DashboardFilters,
  ConversationCountResponse,
  FeedbackSummaryResponse,
  FeedbackOverTimeResponse,
  QuestionsByPlanItem,
  TopQuestionItem,
  ActiveUsersResponse,
  ConversationsOverTimeResponse,
  NegativeFeedbackItem,
  ConversationDetail,
  LLMSummaryResponse,
  Plan,
  UserFilterOption,
  PerformanceSummaryResponse,
  SlowestAnswerItem,
  RecentMessageItem,
  QuarterlySubscribersResponse,
  CostBasis
} from '../api/models'
import { appStateReducer } from './AppReducer'

export interface DashboardData {
  conversationCount: ConversationCountResponse | null
  feedbackSummary: FeedbackSummaryResponse | null
  feedbackOverTime: FeedbackOverTimeResponse | null
  questionsByPlan: QuestionsByPlanItem[] | null
  topQuestions: TopQuestionItem[] | null
  activeUsers: ActiveUsersResponse | null
  timeline: ConversationsOverTimeResponse | null
}

export interface PerformanceData {
  telemetryAvailable: boolean
  summary: PerformanceSummaryResponse
  slowest: SlowestAnswerItem[]
  slowestCostBasis: CostBasis
  recentMessages: RecentMessageItem[]
  recentMessagesCostBasis: CostBasis
}

export interface AppState {
  filters: DashboardFilters
  plans: Plan[]
  users: UserFilterOption[]
  error: string | null

  // Page caches
  dashboardData: DashboardData | null
  dashboardFilters: DashboardFilters | null
  dashboardLoading: boolean

  performanceData: PerformanceData | null
  performanceFilters: DashboardFilters | null

  krData: QuarterlySubscribersResponse | null
  krEnvironment: string | null
  krFetchedAt: number | null

  feedbackData: NegativeFeedbackItem[] | null
  feedbackFilters: DashboardFilters | null
  feedbackType: string | undefined

  conversationsData: ConversationDetail[] | null
  conversationsFilters: DashboardFilters | null

  llmData: LLMSummaryResponse | null
  llmStartDate: string | null
  llmEndDate: string | null
}

export type Action =
  | { type: 'SET_FILTERS'; payload: DashboardFilters }
  | { type: 'SET_DASHBOARD_DATA'; payload: { data: DashboardData; filters: DashboardFilters } }
  | { type: 'SET_DASHBOARD_LOADING'; payload: boolean }
  | { type: 'SET_PERFORMANCE_DATA'; payload: { data: PerformanceData; filters: DashboardFilters } }
  | { type: 'SET_KR_DATA'; payload: { data: QuarterlySubscribersResponse; environment: string; fetchedAt: number } }
  | { type: 'SET_PLANS'; payload: Plan[] }
  | { type: 'SET_USERS'; payload: UserFilterOption[] }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_FEEDBACK_DATA'; payload: { items: NegativeFeedbackItem[]; filters: DashboardFilters; feedbackType?: string } }
  | { type: 'SET_CONVERSATIONS_DATA'; payload: { items: ConversationDetail[]; filters: DashboardFilters } }
  | { type: 'SET_LLM_DATA'; payload: { data: LLMSummaryResponse; startDate: string; endDate: string } }

const getDefaultFilters = (): DashboardFilters => {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - 14)
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  }
}

const initialState: AppState = {
  filters: getDefaultFilters(),
  dashboardData: null,
  dashboardFilters: null,
  dashboardLoading: false,
  performanceData: null,
  performanceFilters: null,
  krData: null,
  krEnvironment: null,
  krFetchedAt: null,
  plans: [],
  users: [],
  error: null,

  feedbackData: null,
  feedbackFilters: null,
  feedbackType: undefined,

  conversationsData: null,
  conversationsFilters: null,

  llmData: null,
  llmStartDate: null,
  llmEndDate: null,
}

export const AppStateContext = createContext<
  | {
      state: AppState
      dispatch: React.Dispatch<Action>
    }
  | undefined
>(undefined)

type AppStateProviderProps = {
  children: ReactNode
}

export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState)

  return <AppStateContext.Provider value={{ state, dispatch }}>{children}</AppStateContext.Provider>
}
